import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import useToggleSidebar from "../../../components/togglesidebar";
import axios from "axios";
import API_URL from "../../../config/config";

import Sidebar from "../../sidebar";
import Header from "../../Header";
import Footer from "../../footer";

function Edit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "admin",
    isActive: true,
  });

  const { isSidebarOpen, toggleSidebar } = useToggleSidebar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem("adminToken");
        if (!token) {
          setError("Unauthorized: No token provided");
          setLoading(false);
          return;
        }

        // Fetch user from admin users endpoint
        const response = await axios.get(`${API_URL}/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success && response.data.users) {
          const userData = response.data.users.find(user => user._id === id);
          if (userData) {
            setFormData({
              name: userData.name || "",
              email: userData.email || "",
              password: "",
              role: userData.role || "admin",
              isActive: userData.isActive !== false,
            });
          } else {
            setError("User not found");
          }
        } else {
          setError(response.data.message || "Failed to fetch user data");
        }
      } catch (error) {
        setError(error.response?.data?.message || "Server Error");
      } finally {
        setLoading(false);
        setLoadingUser(false);
      }
    };

    fetchUser();
  }, [id]);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Name is required.";
    }
    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email address is invalid.";
    }
    // Password is optional for edit, but if provided, it must meet length requirements
    if (formData.password && formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters long.";
    }
    if (!formData.role) {
      errors.role = "Role is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    if (validationErrors[e.target.name]) {
      setValidationErrors(prevErrors => ({
        ...prevErrors,
        [e.target.name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        setError("Unauthorized: No token provided");
        setLoading(false);
        return;
      }

      // Update user details (name, role, isActive)
      await axios.patch(`${API_URL}/users/${id}`, {
        name: formData.name,
        role: formData.role,
        isActive: formData.isActive
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Update password if provided
      if (formData.password && formData.password.trim()) {
        await axios.patch(`${API_URL}/users/${id}/password`, { password: formData.password }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        navigate("/admin/user");
      }, 1500);
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const backendErrors = {};
        error.response.data.errors.forEach(err => {
          backendErrors[err.path] = err.msg;
        });
        setValidationErrors(backendErrors);
        setError(error.response.data.message || "Validation failed on server.");
      } else {
        setError(error.response?.data?.message || "Server Error");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingUser) {
    return <div></div>;
  }

  if (error && !loadingUser && !Object.keys(validationErrors).length) {
    return <div className="alert alert-danger">Error: {error}</div>;
  }

  return (
    <>
      <Helmet>
        <title>Admin | Edit Admin User</title>
      </Helmet>
      <Sidebar isSidebarOpen={isSidebarOpen} />
      <div
        className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`}
        onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}
      >
        <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
          <Header toggleSidebar={toggleSidebar} />
          <div className="breadcrumb-main-bx">
            <div className="breadcrumb-bx">
              <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
              <div className="breadcrumb-link breadcrumb-disabled">/</div>
              <Link className="breadcrumb-link" to="/admin/user">User</Link>
              <div className="breadcrumb-link breadcrumb-disabled">/</div>
              <div className="breadcrumb-link breadcrumb-active">Edit Admin User</div>
            </div>
          </div>
          <section className="content">
            <div className="container-fluid">
              {success && (
                <div className="alert alert-success" role="alert">
                  Updated successfully!
                </div>
              )}

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header">Edit Admin User Details</div>
                    <div className="card-body">
                      <form onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="form-group col-6">
                            <label htmlFor="name">Name <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                            />
                            {validationErrors.name && <div className="invalid-feedback">{validationErrors.name}</div>}
                          </div>
                          <div className="form-group col-6">
                            <label htmlFor="email">Email <span className="text-danger">*</span></label>
                            <input
                              type="email"
                              className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              disabled
                            />
                            {validationErrors.email && <div className="invalid-feedback">{validationErrors.email}</div>}
                            <small className="form-text text-muted">Email cannot be changed after creation</small>
                          </div>
                          <div className="form-group col-6">
                            <label htmlFor="password">New Password</label>
                            <input
                              type="password"
                              className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
                              id="password"
                              name="password"
                              placeholder="Leave blank to keep unchanged"
                              value={formData.password}
                              onChange={handleChange}
                            />
                            {validationErrors.password && <div className="invalid-feedback">{validationErrors.password}</div>}
                            <small className="form-text text-muted">Leave blank to keep current password</small>
                          </div>
                          <div className="form-group col-6">
                            <label htmlFor="role">Role <span className="text-danger">*</span></label>
                            <select
                              className={`form-control ${validationErrors.role ? 'is-invalid' : ''}`}
                              id="role"
                              name="role"
                              value={formData.role}
                              onChange={handleChange}
                            >
                              <option value="admin">Admin</option>
                              <option value="superadmin">Super Admin</option>
                            </select>
                            {validationErrors.role && <div className="invalid-feedback">{validationErrors.role}</div>}
                          </div>
                          <div className="form-group col-6">
                            <label>
                              <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleChange}
                                style={{ marginRight: "5px" }}
                              />
                              Active
                            </label>
                          </div>
                        </div>
                        <button type="submit" className="common-submit-btn mt-2" disabled={loading}>
                          {loading ? "Updating..." : "Update Admin"}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default Edit;
